import { twJoin } from 'tailwind-merge';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';

import { ClassNameComponent } from '@/utils/ClassNameComponent';
import CoursePath from '@/models/CoursePath';
import User, { UserTypeEnum } from '@/models/User';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Skeleton from '@/components/ui/custom/Skeleton';
import useCourseEditing from '@/data/hook/useCourseEditing';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import { isContentCreator } from '@/functions/auth';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { IconWithText } from '@/components/ui/custom/dataDisplay/IconWithText';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { BranchIcon, LoadingIcon } from '@/components/icons';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { preventDefault } from '@/utils/mouseEvent';

export type Props = {
  className?: ClassNameComponent;
  coursePathCollection: CoursePath[];
  loading?: boolean;
  handleSelectCoursePath?: (coursePath: CoursePath) => void;
  user?: User;
  coursePath: CoursePath;
};

export default function CoursePathCardActions(props: Props) {
  const {
    coursePathCollection,
    loading,
    handleSelectCoursePath,
    user,
    coursePath,
  } = props;

  type LoadingTypes = 'none' | 'versioning' | 'editing';
  const [isLoadingRequest, setIsLoadingRequest] =
    useState<LoadingTypes>('none');

  const [t] = useTranslation('translation', { keyPrefix: 'coursesView.admin' });
  const [tCommon] = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const isPublished = coursePath.status === VersioningStatusEnum.PUBLISHED;
  const isFirstVersion = coursePath.version <= 1;
  const versioningCourse = coursePathCollection.some(({ status }) =>
    [VersioningStatusEnum.VERSIONING, VersioningStatusEnum.BETA].includes(
      status,
    ),
  );
  const hasVersioningCourse = !!versioningCourse;
  const edittingCourse = coursePathCollection.find(
    ({ status }) => status === VersioningStatusEnum.EDITING,
  );
  const hasEditingCourse = !!edittingCourse;
  const editText = hasEditingCourse ? 'continueEdit' : 'createEdit';
  const versioningText = hasVersioningCourse
    ? 'continueVersion'
    : 'createVersion';
  const isCreatingFirstVersion = isFirstVersion && !isPublished;
  const { allowVersioningMode, allowEditingMode } = useCourseEditing();
  const queryClient = useQueryClient();

  function getMostRecentPublishedCourse() {
    return coursePathCollection.find(
      ({ status }) => status === VersioningStatusEnum.PUBLISHED,
    );
  }

  async function actionDecorator(
    loading: LoadingTypes,
    cb: () => Promise<void>,
  ) {
    try {
      setIsLoadingRequest(loading);
      await cb();
      await queryClient.invalidateQueries({
        queryKey: coursePathsQueryKeys.list._def,
      });
    } catch (error) {
      alert.error(getErrorMessage(error));
    } finally {
      setIsLoadingRequest('none');
    }
  }

  async function createCourseVersion() {
    const course = coursePathCollection.find(
      ({ status }) => status === VersioningStatusEnum.VERSIONING,
    ) ||
      getMostRecentPublishedCourse() || {
        status: coursePath.status,
        slug: coursePath.slug,
        hasLessonBlocks: coursePath.hasLessonBlocks,
      };
    await actionDecorator('versioning', () =>
      allowVersioningMode(course, isFirstVersion),
    );
  }
  async function createCourseEdit() {
    const course = edittingCourse ||
      getMostRecentPublishedCourse() || {
        slug: coursePath.slug,
        status: coursePath.status,
        hasLessonBlocks: coursePath.hasLessonBlocks,
      };
    await actionDecorator('editing', () =>
      allowEditingMode(course, isFirstVersion),
    );
  }
  const disabledButtons = isLoadingRequest !== 'none';
  const isContentCreatorUser = isContentCreator(user?.userType);

  return (
    <ComponentGuard
      roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.CONTENT_CREATOR]}
    >
      <section
        className="flex flex-col gap-0.5 truncate w-full relative"
        data-testid="versiong-actions"
        onClick={preventDefault}
      >
        <ConditionalRenderer
          condition={!loading}
          fallback={
            <Fragment>
              <Skeleton className="h-3 w-4/5 rounded-full bg-primary-content" />
              <Skeleton className="h-3 w-4/5 rounded-full bg-primary-content" />
            </Fragment>
          }
        >
          <ConditionalRenderer condition={!isCreatingFirstVersion}>
            <TextButton
              disabled={disabledButtons}
              onClick={createCourseEdit}
              text={t(editText)}
              isLoading={isLoadingRequest === 'editing'}
              type="edit"
            />
            <TextButton
              disabled={disabledButtons}
              onClick={createCourseVersion}
              text={t(versioningText)}
              type="edit"
              isLoading={isLoadingRequest === 'versioning'}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={isCreatingFirstVersion}>
            <TextButton
              disabled={disabledButtons}
              onClick={createCourseVersion}
              text={t('continueCreating')}
              type="edit"
              isLoading={isLoadingRequest === 'versioning'}
            />
            <TextButton
              onClick={() => handleSelectCoursePath?.(coursePath)}
              disabled={disabledButtons || isContentCreatorUser}
              text={t('discardCourse')}
              type="discard"
              tooltip={
                isContentCreatorUser ? tCommon('forbiddenAction') : undefined
              }
            />
          </ConditionalRenderer>
        </ConditionalRenderer>
      </section>
    </ComponentGuard>
  );
}

type TextButtonProps = {
  text: string;
  type: 'discard' | 'edit';
  tooltip?: string;
  onClick?: () => void | Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
};

function TextButton(props: TextButtonProps) {
  const { text, type, tooltip, onClick, disabled, isLoading } = props;

  const isDiscardType = type === 'discard';
  const color = isDiscardType ? '!text-error' : '!text-primary';

  const icon = isDiscardType ? (
    <TrashIcon className="h-4 w-4 shrink-0" strokeWidth={3} />
  ) : (
    <BranchIcon className="h-4 w-4 shrink-0" strokeWidth={2.8} />
  );

  return (
    <ConditionalWrapper
      condition={!!tooltip}
      wrapper={<Tooltip text={tooltip} />}
    >
      <IconWithText
        onClick={onClick}
        role="button"
        text={text}
        icon={!isLoading ? icon : <LoadingIcon className="w-4" />}
        className={{
          text: 'relative truncate text-14 sm:text-16',
          base: twJoin(
            `w-fit max-w-full gap-1 cursor-pointer flex-row-reverse`,
            color,
            disabled && 'disabled',
          ),
        }}
      />
    </ConditionalWrapper>
  );
}
